import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/utils';
import { cn } from '../../lib/utils';
import { ICON } from '../ui';
import { BsnField } from './BsnField';
import {
  RAHIEM_SEED,
  RAHIEM_TRIGION_SEED,
  hasPersoondataImported,
  normalizeDutchPostcode,
  type OnboardingData,
} from './shared';

// --- Shared form helpers ----------------------------------------------------

interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

export function Field({ label, htmlFor, hint, error, required, children, icon }: FieldProps) {
  return (
    <div>
      <label htmlFor={htmlFor} className="label flex items-center gap-1.5">
        {icon}
        {label}
        {required && <span className="text-red-300">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-300">{error}</p>}
      {!error && hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function AutoFillBar({ onClick, label, show }: { onClick: () => void; label: string; show: boolean }) {
  if (!show) return null;
  return (
    <div className="mb-4">
      <button type="button" onClick={onClick} className="btn-secondary w-full sm:w-auto">
        {label}
      </button>
    </div>
  );
}

function Lead({ children }: { children: React.ReactNode }) {
  return <p className="t-body max-w-xl">{children}</p>;
}

function Note({ children }: { children: React.ReactNode }) {
  return <p className="t-muted">{children}</p>;
}

// --- Step components --------------------------------------------------------

export function Step1Welcome({ profile }: { profile: any }) {
  const naam = profile?.voornaam ?? 'Rahiem';
  return (
    <div className="space-y-4">
      <h2>Welkom, {naam}</h2>
      <Lead>
        In twaalf korte stappen maak je je profiel compleet. Elke stap wordt bewaard, dus je kunt altijd later verder.
      </Lead>
      <ul className="list -mx-4 border-y border-surface-line/60 sm:-mx-5">
        <li className="row text-sm">
          <span className="min-w-0 flex-1 text-slate-400">Verplicht</span>
          <span className="text-right text-slate-100">Naam, personeelsnummer, datum in dienst</span>
        </li>
        <li className="row text-sm">
          <span className="min-w-0 flex-1 text-slate-400">Later aanvullen</span>
          <span className="text-right text-slate-100">Alles in Instellingen</span>
        </li>
      </ul>
      <div className="flex items-start gap-2 text-sm text-slate-400">
        <Lock className={cn(ICON.sm, 'mt-0.5 shrink-0')} aria-hidden />
        <span>BSN, telefoon, geboortedatum en adres worden versleuteld opgeslagen.</span>
      </div>
    </div>
  );
}

export function Step2Naw({
  data,
  profile,
  onChange,
  errors,
}: {
  data: OnboardingData;
  profile: any;
  onChange: (patch: Partial<OnboardingData['naw']>) => void;
  errors: Record<string, string>;
}) {
  const d = { ...profile, ...data.naw };
  const set = (k: string, v: string) => onChange({ ...data.naw, [k]: v });
  const showAutoFill = !hasPersoondataImported(profile) && !data.meta?.persoondata_imported;

  return (
    <div>
      <AutoFillBar
        show={showAutoFill}
        onClick={() => onChange({
          voornaam: RAHIEM_SEED.voornaam,
          achternaam: RAHIEM_SEED.achternaam,
          roepnaam: RAHIEM_SEED.roepnaam,
        })}
        label="Mijn naam invullen"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Voornaam" htmlFor="voornaam" required error={errors.voornaam}>
          <input
            id="voornaam"
            className={cn('input', errors.voornaam && 'border-red-500')}
            value={d.voornaam ?? ''}
            onChange={(e) => set('voornaam', e.target.value)}
            autoComplete="given-name"
          />
        </Field>
        <Field label="Achternaam" htmlFor="achternaam" required error={errors.achternaam}>
          <input
            id="achternaam"
            className={cn('input', errors.achternaam && 'border-red-500')}
            value={d.achternaam ?? ''}
            onChange={(e) => set('achternaam', e.target.value)}
            autoComplete="family-name"
          />
        </Field>
        <Field label="Roepnaam" htmlFor="roepnaam" hint="Optioneel">
          <input
            id="roepnaam"
            className="input"
            value={d.roepnaam ?? ''}
            onChange={(e) => set('roepnaam', e.target.value)}
          />
        </Field>
        <Field label="E-mail" hint="Hoort bij je Google-account">
          <input type="email" className="input opacity-60" value={profile?.email ?? ''} readOnly />
        </Field>
      </div>
    </div>
  );
}

export function Step3Contact({
  data,
  profile,
  onChange,
  errors,
}: {
  data: OnboardingData;
  profile: any;
  onChange: (patch: Partial<OnboardingData['contact']>) => void;
  errors: Record<string, string>;
}) {
  const d = { ...profile, ...data.contact };
  const set = (k: string, v: string) => onChange({ ...data.contact, [k]: v });
  const showAutoFill = !hasPersoondataImported(profile) && !data.meta?.persoondata_imported;

  return (
    <div>
      <AutoFillBar
        show={showAutoFill}
        onClick={() => onChange({
          phone: RAHIEM_SEED.phone,
          birth_place: RAHIEM_SEED.birth_place,
          birth_country: RAHIEM_SEED.birth_country,
        })}
        label="Mijn contactgegevens invullen"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Telefoon" htmlFor="phone" error={errors.phone} hint="Bijvoorbeeld 0612345678">
          <input
            id="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            className={cn('input', errors.phone && 'border-red-500')}
            value={d.phone ?? ''}
            onChange={(e) => set('phone', e.target.value)}
          />
        </Field>
        <div className="hidden sm:block" />
        <Field label="Geboorteplaats" htmlFor="birth_place">
          <input
            id="birth_place"
            className="input"
            value={d.birth_place ?? ''}
            onChange={(e) => set('birth_place', e.target.value)}
          />
        </Field>
        <Field label="Geboorteland" htmlFor="birth_country">
          <input
            id="birth_country"
            className="input"
            value={d.birth_country ?? 'Nederland'}
            onChange={(e) => set('birth_country', e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

export function Step4Sensitive({
  data,
  profile,
  onChange,
  errors,
}: {
  data: OnboardingData;
  profile: any;
  onChange: (patch: Partial<OnboardingData['sensitive']>) => void;
  errors: Record<string, string>;
}) {
  const d = { ...profile, ...data.sensitive };
  const set = (k: string, v: string) => onChange({ ...data.sensitive, [k]: v });
  const showAutoFill = !hasPersoondataImported(profile) && !data.meta?.persoondata_imported;

  return (
    <div>
      <div className="mb-4 flex items-start gap-2 text-sm text-slate-400">
        <Lock className={cn(ICON.sm, 'mt-0.5 shrink-0')} aria-hidden />
        <span>Versleuteld opgeslagen, alleen zichtbaar als je bent ingelogd.</span>
      </div>
      <AutoFillBar
        show={showAutoFill}
        onClick={() => onChange({
          bsn: RAHIEM_SEED.bsn,
          birth_date: RAHIEM_SEED.birth_date,
          address_street: RAHIEM_SEED.address_street,
          address_postcode: RAHIEM_SEED.address_postcode,
          address_city: RAHIEM_SEED.address_city,
        })}
        label="Mijn persoonsgegevens invullen"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="BSN" htmlFor="bsn" error={errors.bsn} hint="9 cijfers">
          <BsnField
            id="bsn"
            value={d.bsn ?? ''}
            onChange={(v) => set('bsn', v)}
            error={errors.bsn}
          />
        </Field>
        <Field label="Geboortedatum" htmlFor="birth_date" error={errors.birth_date}>
          <input
            id="birth_date"
            type="date"
            lang="nl"
            placeholder="dd-mm-jjjj"
            className={cn('input', errors.birth_date && 'border-red-500')}
            value={d.birth_date ?? ''}
            onChange={(e) => set('birth_date', e.target.value)}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Straat en huisnummer" htmlFor="address_street">
            <input
              id="address_street"
              className="input"
              value={d.address_street ?? ''}
              onChange={(e) => set('address_street', e.target.value)}
              autoComplete="street-address"
            />
          </Field>
        </div>
        <Field label="Postcode" htmlFor="address_postcode" error={errors.address_postcode}>
          <input
            id="address_postcode"
            className={cn('input', errors.address_postcode && 'border-red-500')}
            value={d.address_postcode ?? ''}
            onChange={(e) => set('address_postcode', e.target.value)}
            onBlur={(e) => e.target.value && set('address_postcode', normalizeDutchPostcode(e.target.value))}
            autoComplete="postal-code"
            placeholder="1234 AB"
          />
        </Field>
        <Field label="Woonplaats" htmlFor="address_city">
          <input
            id="address_city"
            className="input"
            value={d.address_city ?? ''}
            onChange={(e) => set('address_city', e.target.value)}
            autoComplete="address-level2"
          />
        </Field>
      </div>
    </div>
  );
}

export function Step5Werkgever({
  data,
  profile,
  onChange,
  errors,
}: {
  data: OnboardingData;
  profile: any;
  onChange: (patch: Partial<OnboardingData['werkgever']>) => void;
  errors: Record<string, string>;
}) {
  const d = { ...profile, ...data.werkgever };
  const set = (k: string, v: string) => onChange({ ...data.werkgever, [k]: v });
  const showAutoFill = !hasPersoondataImported(profile) && !data.meta?.persoondata_imported;

  return (
    <div>
      <AutoFillBar
        show={showAutoFill}
        onClick={() => onChange({ ...RAHIEM_TRIGION_SEED })}
        label="Mijn Trigion-gegevens invullen"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Personeelsnummer" htmlFor="personeelsnummer" required error={errors.personeelsnummer}>
          <input
            id="personeelsnummer"
            className={cn('input font-mono', errors.personeelsnummer && 'border-red-500')}
            value={d.personeelsnummer ?? ''}
            onChange={(e) => set('personeelsnummer', e.target.value)}
          />
        </Field>
        <Field label="Datum in dienst" htmlFor="datum_in_dienst" required error={errors.datum_in_dienst}>
          <input
            id="datum_in_dienst"
            type="date"
            lang="nl"
            placeholder="dd-mm-jjjj"
            className={cn('input', errors.datum_in_dienst && 'border-red-500')}
            value={d.datum_in_dienst ?? ''}
            onChange={(e) => set('datum_in_dienst', e.target.value)}
          />
        </Field>
        <Field label="Manager" htmlFor="manager_naam">
          <input
            id="manager_naam"
            className="input"
            value={d.manager_naam ?? ''}
            onChange={(e) => set('manager_naam', e.target.value)}
          />
        </Field>
        <Field label="Praktijkopleider" htmlFor="praktijkopleider_naam">
          <input
            id="praktijkopleider_naam"
            className="input"
            value={d.praktijkopleider_naam ?? ''}
            onChange={(e) => set('praktijkopleider_naam', e.target.value)}
          />
        </Field>
        <Field label="Afdeling" htmlFor="afdeling">
          <input
            id="afdeling"
            className="input"
            value={d.afdeling ?? ''}
            onChange={(e) => set('afdeling', e.target.value)}
          />
        </Field>
        <Field label="Plangebied" htmlFor="plangebied">
          <input
            id="plangebied"
            className="input"
            value={d.plangebied ?? ''}
            onChange={(e) => set('plangebied', e.target.value)}
          />
        </Field>
        <Field label="Kostenplaats" htmlFor="kostenplaats">
          <input
            id="kostenplaats"
            className="input"
            value={d.kostenplaats ?? ''}
            onChange={(e) => set('kostenplaats', e.target.value)}
          />
        </Field>
        <Field label="Functiecode" htmlFor="functiecode">
          <input
            id="functiecode"
            className="input font-mono"
            value={d.functiecode ?? ''}
            onChange={(e) => set('functiecode', e.target.value)}
          />
        </Field>
        <Field label="Studentnummer SVPB" htmlFor="studentnummer" hint="Alleen als je een opleiding volgt">
          <input
            id="studentnummer"
            className="input font-mono"
            value={d.studentnummer ?? ''}
            onChange={(e) => set('studentnummer', e.target.value)}
          />
        </Field>
        <Field label="Profitcenter" htmlFor="profitcenter">
          <input
            id="profitcenter"
            className="input font-mono"
            value={d.profitcenter ?? ''}
            onChange={(e) => set('profitcenter', e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

export function Step6Contract() {
  return (
    <div className="space-y-3">
      <Lead>
        Soort contract, contracturen en start- en einddatum worden ingelezen uit je arbeidsovereenkomst. Je vindt
        ze daarna onder Documenten.
      </Lead>
      <Note>Je hoeft hier niets in te vullen.</Note>
    </div>
  );
}

export function Step7Objects() {
  const { data: objects = [] } = useQuery({ queryKey: ['objects'], queryFn: api.objects.list });

  return (
    <div className="space-y-4">
      <Lead>De locaties waar je diensten draait. Toevoegen kan later in Instellingen, onder Objecten.</Lead>
      {objects.length === 0 ? (
        <Note>Nog geen werklocaties. Ze komen vanzelf binnen met je urencontroles.</Note>
      ) : (
        <>
          <ul className="list -mx-4 max-h-72 overflow-y-auto border-y border-surface-line/60 sm:-mx-5">
            {objects.map((o) => (
              <li key={o.id} className="row">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-slate-100">{o.naam}</div>
                  <div className="truncate text-xs text-slate-400">{[o.postcode, o.objectnummer].filter(Boolean).join(' · ') || 'Geen postcode'}</div>
                </div>
                <span className={cn('t-num shrink-0 text-sm', o.distance_km != null ? 'text-slate-100' : 'text-slate-500')}>
                  {o.distance_km != null ? `${String(o.distance_km).replace('.', ',')} km` : 'Onbekend'}
                </span>
              </li>
            ))}
          </ul>
          <Note>
            {objects.length} {objects.length === 1 ? 'werklocatie' : 'werklocaties'}
          </Note>
        </>
      )}
    </div>
  );
}

export function Step8Diplomas() {
  return (
    <div className="space-y-4">
      <Lead>Je diploma's en certificaten worden automatisch ingelezen zodra ze in de app staan.</Lead>
      <ul className="list -mx-4 border-y border-surface-line/60 sm:-mx-5">
        {[
          { naam: 'MBO Beveiliger 2', beschrijving: 'Vakopleiding' },
          { naam: 'BHV Basis', beschrijving: 'Jaarlijkse herhaling verplicht' },
          { naam: 'BHV Ploegleider', beschrijving: 'Jaarlijks' },
          { naam: 'ESO', beschrijving: 'Evenementenbeveiliging' },
        ].map((d) => (
          <li key={d.naam} className="row text-sm">
            <span className="min-w-0 flex-1 font-medium text-slate-100">{d.naam}</span>
            <span className="text-right text-slate-400">{d.beschrijving}</span>
          </li>
        ))}
      </ul>
      <Note>Het overzicht waarschuwt als een diploma binnen 90 dagen verloopt.</Note>
    </div>
  );
}

export function Step9Salary() {
  const today = new Date().toISOString().slice(0, 10);
  const { data: wages } = useQuery({ queryKey: ['cao-wages', today], queryFn: () => api.cao.wages(today) });

  return (
    <div className="space-y-4">
      <Lead>Schaal, trede, uurloon en contracturen komen uit je contract en je loonstroken.</Lead>
      {wages && (
        <div>
          <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
            <h3>Schaal 3 (Beveiliger A)</h3>
            <span className="t-label">Cao {wages.cao_version}</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th className="!px-0">Trede</th>
                <th className="r !px-0">Uurloon</th>
              </tr>
            </thead>
            <tbody>
              {wages.wages
                .filter((w) => w.schaal === 3)
                .slice(0, 5)
                .map((w) => (
                  <tr key={w.trede}>
                    <td className="!px-0 text-slate-400">Trede {w.trede}</td>
                    <td className="r !px-0 font-medium text-slate-100">{formatMoney(w.uurloon)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      <Note>Je loon en je volgende verhoging staan later in Instellingen, onder Werkgever.</Note>
    </div>
  );
}

export function Step10Iban({
  data,
  profile,
  onChange,
  errors,
}: {
  data: OnboardingData;
  profile: any;
  onChange: (patch: Partial<OnboardingData['iban']>) => void;
  errors: Record<string, string>;
}) {
  const d = { ...profile, ...data.iban };
  return (
    <div className="space-y-4">
      <Lead>Je IBAN wordt ook van je loonstrook gelezen. Vul hem hier in als je dat nu al wilt.</Lead>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="IBAN" htmlFor="iban" error={errors.iban} hint="Spaties zijn niet nodig">
            <input
              id="iban"
              className={cn('input font-mono uppercase', errors.iban && 'border-red-500')}
              value={d.iban ?? ''}
              onChange={(e) => onChange({ iban: e.target.value.toUpperCase() })}
              placeholder="NL00 BANK 0000 0000 00"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

export function Step11Preferences() {
  return (
    <div className="space-y-4">
      <Lead>Deze instellingen liggen vast, zodat alles overeenkomt met de stukken van Trigion.</Lead>
      <dl className="list -mx-4 border-y border-surface-line/60 sm:-mx-5">
        {[
          { k: 'Taal', v: 'Nederlands' },
          { k: 'Thema', v: 'Donker' },
          { k: 'Week begint op', v: 'Maandag' },
          { k: 'Datum', v: 'dd-mm-jjjj' },
          { k: 'Tijd', v: '24-uurs' },
          { k: 'Valuta', v: 'Euro' },
        ].map((p) => (
          <div key={p.k} className="row text-sm">
            <dt className="min-w-0 flex-1 text-slate-400">{p.k}</dt>
            <dd className="text-slate-100">{p.v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ---- Step 12: samenvatting per onderdeel, met Wijzig naar die stap -----------

interface SummaryProps {
  data: OnboardingData;
  profile: any;
  onJumpToStep: (step: number) => void;
  onEdit: (section: keyof OnboardingData, patch: any) => void;
}

export function Step12Summary({ data, profile, onJumpToStep, onEdit: _onEdit }: SummaryProps) {
  const { data: objects = [] } = useQuery({ queryKey: ['objects'], queryFn: api.objects.list });
  const merged = { ...profile, ...data.naw, ...data.contact, ...data.sensitive, ...data.werkgever, ...data.iban };
  const leeg = '–';

  const groups: Array<{ title: string; step: number; rows: Array<[string, string]> }> = [
    {
      title: 'Persoon',
      step: 2,
      rows: [
        ['Naam', `${merged.voornaam ?? leeg} ${merged.achternaam ?? ''}${merged.roepnaam ? ` (${merged.roepnaam})` : ''}`.trim()],
        ['Telefoon', merged.phone ?? leeg],
        ['Geboorteplaats', merged.birth_place ?? leeg],
      ],
    },
    {
      title: 'Persoonsgegevens',
      step: 4,
      rows: [
        ['BSN', merged.bsn ? `●●●●●${String(merged.bsn).slice(-4)}` : leeg],
        ['Geboortedatum', merged.birth_date ? formatDate(merged.birth_date) : leeg],
        ['Adres', [merged.address_street, merged.address_postcode, merged.address_city].filter(Boolean).join(', ') || leeg],
      ],
    },
    {
      title: 'Werkgever',
      step: 5,
      rows: [
        ['Personeelsnummer', merged.personeelsnummer ?? leeg],
        ['In dienst sinds', merged.datum_in_dienst ? formatDate(merged.datum_in_dienst) : leeg],
        ['Manager', merged.manager_naam ?? leeg],
        ['Afdeling', merged.afdeling ?? leeg],
        ['Werklocaties', `${objects.length} ${objects.length === 1 ? 'locatie' : 'locaties'}`],
      ],
    },
    {
      title: 'Bank',
      step: 10,
      rows: [['IBAN', merged.iban ? String(merged.iban).replace(/(.{4})/g, '$1 ').trim() : leeg]],
    },
  ];

  return (
    <div className="space-y-5">
      <Lead>Kijk je gegevens na. Met Wijzig ga je terug naar die stap.</Lead>
      {groups.map((g) => (
        <section key={g.title}>
          <div className="flex items-center justify-between gap-3">
            <h3>{g.title}</h3>
            <button type="button" onClick={() => onJumpToStep(g.step)} className="btn-ghost btn-sm" aria-label={`${g.title} wijzigen`}>
              Wijzig
            </button>
          </div>
          <dl className="mt-1 divide-y divide-surface-line/60 text-sm">
            {g.rows.map(([label, value]) => (
              <div key={label} className="flex items-baseline gap-3 py-2">
                <dt className="w-32 shrink-0 text-slate-400 sm:w-40">{label}</dt>
                <dd className="min-w-0 flex-1 break-words text-slate-100">{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
